package com.eladfinish.bible.contest.screens.lexicographicalSort

import com.eladfinish.bible.contest.analytics.AnalyticsConstants.LexicographicalSort
import com.eladfinish.bible.contest.analytics.providers.AnalyticsProvider
import com.eladfinish.bible.contest.arch.ViewModelStore
import com.eladfinish.bible.contest.common.classNameOf
import com.eladfinish.bible.contest.common.gender.getHebrewGender
import com.eladfinish.bible.contest.constants.Constants
import com.eladfinish.bible.contest.screens.browse.tableOfContents.model.OutputChapters
import com.eladfinish.bible.contest.screens.browse.usecases.GenerateHtmlToPdfUseCase
import com.eladfinish.bible.contest.screens.browse.usecases.OpenPdfUseCase
import com.eladfinish.bible.contest.screens.browse.usecases.PrepareHtmlForPdfUseCase
import com.eladfinish.bible.contest.screens.chapterComplete.usecases.BuildLexicographicalTableHtmlUseCase
import com.eladfinish.bible.contest.screens.lexicographicalSort.usecases.SavePdfBytesUseCase
import com.eladfinish.bible.contest.screens.lexicographicalSort.usecases.SendHtmlEmailUseCase
import com.eladfinish.bible.contest.screens.mikud.MikudRepository
import com.eladfinish.bible.contest.screens.mikud.usecases.GetChaptersInMikudUseCase
import com.eladfinish.contract.email.SendHtmlEmailRequest
import com.russhwolf.settings.Settings
import io.github.aakira.napier.Napier
import kotlinx.coroutines.flow.MutableStateFlow
import kotlin.coroutines.cancellation.CancellationException

class LexicographicalSortPdfViewerStore(
    private val params: Params,
    private val logicUseCase: LexicographicalSortLogicUseCase,
    private val buildLexicographicalTableHtmlUseCase: BuildLexicographicalTableHtmlUseCase,
    private val prepareHtmlForPdfUseCase: PrepareHtmlForPdfUseCase,
    private val generateHtmlToPdfUseCase: GenerateHtmlToPdfUseCase,
    private val savePdfBytesUseCase: SavePdfBytesUseCase,
    private val openPdfUseCase: OpenPdfUseCase,
    private val sendHtmlEmailUseCase: SendHtmlEmailUseCase,
    private val mikudRepository: MikudRepository,
    private val getChaptersInMikudUseCase: GetChaptersInMikudUseCase,
    private val analyticsProvider: AnalyticsProvider,
    private val settings: Settings,
) : ViewModelStore<LexicographicalSortPdfViewerState, LexicographicalSortPdfViewerAction, LexicographicalSortPdfViewerEffect>(
    tag = "LexicographicalSortPdfViewerStore",
) {

    companion object {
        data class Params(
            val bookIndex: Int,
            val chapterIndex: Int,
            val outputChaptersOrdinal: Int,
        )
    }

    private val outputChapters: OutputChapters
        get() = OutputChapters.entries[params.outputChaptersOrdinal]

    private val tableParams: Map<String, Any>
        get() = lexicographicalSortParams(outputChapters = outputChapters, bookNumber = params.bookIndex)

    override val stateFlow = MutableStateFlow(
        LexicographicalSortPdfViewerState(
            htmlState = LexicographicalSortPdfViewerState.HtmlState.Loading,
            savedPdfName = null,
            savedPdfUri = null,
            canOpenPdf = openPdfUseCase.canOpenPdf,
            isLoading = false,
            pdfEmailDialogState = LexicographicalSortPdfViewerState.PdfEmailDialogState.Hidden,
            outputChapters = outputChapters,
        )
    )

    init {
        launchEffect {
            try {
                val result = buildLexicographicalTableHtmlUseCase(
                    outputChapters = outputChapters,
                    indexPickedBook = params.bookIndex,
                    indexPickedChapter = params.chapterIndex,
                )
                updateState {
                    it.copy(
                        htmlState = LexicographicalSortPdfViewerState.HtmlState.Ready(
                            fullHtml = result.full,
                            previewHtml = result.preview,
                            previewTable = result.previewTable,
                        )
                    )
                }
            } catch (e: CancellationException) {
                // The user left the screen while the table was being built: not a failure.
                throw e
            } catch (e: Exception) {
                Napier.e(message = "buildLexicographicalTableHtmlUseCase", throwable = e)
                analyticsProvider.logEvent(LexicographicalSort.EVENT_PREVIEW_FAILED)
                updateState {
                    it.copy(htmlState = LexicographicalSortPdfViewerState.HtmlState.Error(e.message ?: "שגיאה ביצירת הטבלה"))
                }
            }
        }
    }

    override fun handleAction(action: LexicographicalSortPdfViewerAction): LexicographicalSortPdfViewerState =
        when (action) {
            LexicographicalSortPdfViewerAction.SavePdfClicked -> handleSavePdfClicked()
            LexicographicalSortPdfViewerAction.EmailPdfClicked -> handleEmailPdfClicked()
            is LexicographicalSortPdfViewerAction.PdfEmailConfirmed -> handlePdfEmailConfirmed(
                firstName = action.firstName,
                lastName = action.lastName,
                email = action.email,
                saveDetails = action.saveDetails,
            )

            LexicographicalSortPdfViewerAction.PdfEmailDialogDismissed ->
                currentState.copy(pdfEmailDialogState = LexicographicalSortPdfViewerState.PdfEmailDialogState.Hidden)

            is LexicographicalSortPdfViewerAction.OpenPdf -> {
                openPdfUseCase(action.uri)
                currentState
            }
        }

    private fun handleSavePdfClicked(): LexicographicalSortPdfViewerState {
        val rawHtml = (currentState.htmlState as? LexicographicalSortPdfViewerState.HtmlState.Ready)?.fullHtml
            ?: return currentState

        analyticsProvider.logEvent(LexicographicalSort.EVENT_SAVE_PDF, tableParams)

        val pdfName = logicUseCase.buildPdfName(
            bookIndex = params.bookIndex,
            chapterIndex = params.chapterIndex,
            outputChapters = outputChapters,
        )

        launchEffect {
            generatePdf(rawHtml = rawHtml) { pdfBytes ->
                savePdfBytesUseCase(
                    pdfBytes = pdfBytes,
                    fileName = pdfName,
                    onSaved = { pdfUri ->
                        analyticsProvider.logEvent(LexicographicalSort.EVENT_SAVE_PDF_SUCCESS, tableParams)
                        launchEffect {
                            updateState {
                                it.copy(
                                    savedPdfName = pdfName,
                                    savedPdfUri = pdfUri,
                                    canOpenPdf = openPdfUseCase.canOpenPdf,
                                    isLoading = false,
                                )
                            }
                            sideEffectFlow.emit(
                                LexicographicalSortPdfViewerEffect.ShowPdfSavedSnackbar(
                                    pdfName = logicUseCase.buildPdfName(
                                        bookIndex = params.bookIndex,
                                        chapterIndex = params.chapterIndex,
                                        outputChapters = outputChapters,
                                        withGershaim = true,
                                    ),
                                    pdfUri = pdfUri,
                                    canOpen = openPdfUseCase.canOpenPdf,
                                )
                            )
                        }
                    },
                    onError = { throwable ->
                        Napier.e(message = "savePdfBytesUseCase", throwable = throwable)
                        logSavePdfFailed(stage = LexicographicalSort.STAGE_SAVE)
                        launchEffect {
                            updateState { it.copy(isLoading = false) }
                            sideEffectFlow.emit(LexicographicalSortPdfViewerEffect.ShowToast("שגיאה בשמירת ה-PDF"))
                        }
                    },
                )
            }
        }

        return currentState.copy(isLoading = true)
    }

    private fun logSavePdfFailed(stage: String) {
        analyticsProvider.logEvent(
            LexicographicalSort.EVENT_SAVE_PDF_FAILED,
            mapOf(LexicographicalSort.PARAM_STAGE to stage),
        )
    }

    private fun handleEmailPdfClicked(): LexicographicalSortPdfViewerState {
        analyticsProvider.logEvent(LexicographicalSort.EVENT_EMAIL_DIALOG_OPENED, tableParams)
        return currentState.copy(
            pdfEmailDialogState = LexicographicalSortPdfViewerState.PdfEmailDialogState.Visible(
                firstName = settings.getString(key = Constants.PREF_FIRST_NAME, defaultValue = ""),
                lastName = settings.getString(key = Constants.PREF_LAST_NAME, defaultValue = ""),
                email = settings.getString(key = Constants.PREF_USER_EMAIL, defaultValue = ""),
                saveDetails = settings.getBoolean(key = Constants.PREF_SAVE_PDF_EMAIL_DETAILS, defaultValue = true),
            )
        )
    }

    private fun handlePdfEmailConfirmed(
        firstName: String,
        lastName: String,
        email: String,
        saveDetails: Boolean,
    ): LexicographicalSortPdfViewerState {
        val rawHtml = (currentState.htmlState as? LexicographicalSortPdfViewerState.HtmlState.Ready)?.fullHtml
            ?: return currentState

        analyticsProvider.logEvent(LexicographicalSort.EVENT_EMAIL_PDF, tableParams)

        settings.putBoolean(Constants.PREF_SAVE_PDF_EMAIL_DETAILS, saveDetails)
        if (saveDetails) {
            settings.putString(Constants.PREF_FIRST_NAME, firstName)
            settings.putString(Constants.PREF_LAST_NAME, lastName)
            settings.putString(Constants.PREF_USER_EMAIL, email)
        }

        val mikudChapterNumbers = if (outputChapters == OutputChapters.BY_MIKUD) {
            getChaptersInMikudUseCase(
                mikudStr = mikudRepository.mikudList,
                bookNumber = params.bookIndex + 1,
            ).map { it.first }
        } else {
            null
        }

        launchEffect {
            sideEffectFlow.emit(
                LexicographicalSortPdfViewerEffect.ShowLoadingSnackbar("שולח את הקובץ למייל…")
            )
            val result = sendHtmlEmailUseCase(
                request = SendHtmlEmailRequest(
                    firstName = firstName,
                    lastName = lastName,
                    email = email,
                    subject = "$firstName, הקובץ שלך מאפליקציית ״הראשון בחידון״ מוכן!",
                    emailBodyHtml = "<div dir=\"rtl\" style=\"text-align:right;\">" +
                            "<p>שלום $firstName $lastName</p>" +
                            "<p>מצורף קובץ ה-PDF שביקשת.<br>" +
                            "עם טבלת פסוקים ממוינים בסדר לקסיקוגרפי.<br>" +
                            "בהצלחה!</p>" +
                            "<p>צוות ״הראשון בחידון״</p>" +
                            "</div>",
                    tableHtml = rawHtml,
                    fileName = "${
                        logicUseCase.buildPdfTitle(
                            bookIndex = params.bookIndex,
                            chapterIndex = params.chapterIndex,
                            outputChapters = outputChapters,
                        )
                    }.pdf",
                    mikudChapterNumbers = mikudChapterNumbers,
                    demo = true,
                    hebrewGender = settings.getHebrewGender().name.lowercase(),
                ),
            )
            sideEffectFlow.emit(LexicographicalSortPdfViewerEffect.DismissSnackbar)
            result.onSuccess {
                analyticsProvider.logEvent(LexicographicalSort.EVENT_EMAIL_PDF_SUCCESS, tableParams)
                sideEffectFlow.emit(LexicographicalSortPdfViewerEffect.ShowToast("המייל נשלח בהצלחה"))
            }.onFailure { throwable ->
                Napier.e(message = "sendHtmlEmailUseCase", throwable = throwable)
                // Leaving the screen cancels the request: the mail may still go out, so it is no failure.
                if (throwable !is CancellationException) {
                    analyticsProvider.logEvent(
                        LexicographicalSort.EVENT_EMAIL_PDF_FAILED,
                        mapOf(LexicographicalSort.PARAM_ERROR_TYPE to classNameOf(throwable::class)),
                    )
                }
                sideEffectFlow.emit(LexicographicalSortPdfViewerEffect.ShowToast(throwable.message ?: "שגיאה בשליחת המייל"))
            }
        }

        return currentState.copy(
            pdfEmailDialogState = LexicographicalSortPdfViewerState.PdfEmailDialogState.Hidden,
        )
    }

    private fun generatePdf(rawHtml: String, onDone: suspend (ByteArray) -> Unit) {
        // Local save: no multiple-of-4 page padding (that's only needed for the printed/booklet
        // server flow). Generate once and hand the bytes back.
        generateHtmlToPdfUseCase(
            htmlContent = prepareHtmlForPdfUseCase(rawHtml),
            onGenerated = { pdfBytes -> launchEffect { onDone(pdfBytes) } },
            onError = { throwable ->
                Napier.e(message = "generateHtmlToPdfUseCase", throwable = throwable)
                logSavePdfFailed(stage = LexicographicalSort.STAGE_GENERATE)
                launchEffect {
                    sideEffectFlow.emit(LexicographicalSortPdfViewerEffect.ShowToast("שגיאה ביצירת ה-PDF"))
                    updateState { it.copy(isLoading = false) }
                }
            },
        )
    }
}
